<template>
  <router-link
    :id="id"
    :to="to"
    class="category-hero-card"
    :aria-label="title"
  >
    <div class="category-hero-card__media">
      <img
        class="category-hero-card__image"
        :src="imageSrc"
        :alt="title"
        loading="lazy"
      >
    </div>
    <div class="category-hero-card__overlay" aria-hidden="true" />
    <div class="category-hero-card__content">
      <h3 class="category-hero-card__title">{{ title }}</h3>
      <span class="category-hero-card__cta">
        {{ $l('homeCategoryExplore') }}
        <span class="category-hero-card__cta-arrow" aria-hidden="true">→</span>
      </span>
    </div>
  </router-link>
</template>

<script>
export default {
  name: 'CategoryHeroCard',
  props: {
    id: {
      type: String,
      default: '',
    },
    categoryKey: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    imageSrc: {
      type: String,
      required: true,
    },
    to: {
      type: String,
      required: true,
    },
  },
};
</script>

<style scoped>
.category-hero-card {
  position: relative;
  display: block;
  overflow: hidden;
  min-height: 13.5rem;
  border-radius: 1.25rem;
  color: #fff;
  text-decoration: none;
  background: #0f172a;
  box-shadow:
    0 1px 3px rgba(15, 23, 42, 0.08),
    0 8px 24px rgba(15, 23, 42, 0.1);
  transition:
    transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    box-shadow 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: transform, box-shadow;
}

.category-hero-card__media {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.category-hero-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1);
  transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: transform;
}

.category-hero-card__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.08) 0%,
    rgba(15, 23, 42, 0.35) 45%,
    rgba(15, 23, 42, 0.72) 100%
  );
  pointer-events: none;
}

.category-hero-card__content {
  position: relative;
  z-index: 1;
  display: flex;
  min-height: 13.5rem;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.25rem 1.25rem 1.125rem;
}

.category-hero-card__title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 12px rgba(15, 23, 42, 0.35);
}

[dir='rtl'] .category-hero-card__content {
  align-items: flex-end;
  text-align: right;
}

[dir='ltr'] .category-hero-card__content {
  align-items: flex-start;
  text-align: left;
}

.category-hero-card__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.625rem;
  width: fit-content;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.92);
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f172a;
  opacity: 1;
  transform: translateY(0);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease,
    background-color 0.2s ease;
}

.category-hero-card__cta-arrow {
  line-height: 1;
}

@media (hover: hover) and (pointer: fine) {
  .category-hero-card__cta {
    opacity: 0;
    transform: translateY(8px);
  }

  .category-hero-card:hover,
  .category-hero-card:focus-visible {
    transform: translateY(-6px);
    box-shadow:
      0 4px 8px rgba(15, 23, 42, 0.08),
      0 20px 40px rgba(15, 23, 42, 0.18);
  }

  .category-hero-card:hover .category-hero-card__image,
  .category-hero-card:focus-visible .category-hero-card__image {
    transform: scale(1.05);
  }

  .category-hero-card:hover .category-hero-card__cta,
  .category-hero-card:focus-visible .category-hero-card__cta {
    opacity: 1;
    transform: translateY(0);
  }

  .category-hero-card:hover .category-hero-card__cta,
  .category-hero-card:focus-visible .category-hero-card__cta {
    background: #fff;
  }
}

@media (min-width: 640px) {
  .category-hero-card {
    min-height: 15rem;
  }

  .category-hero-card__content {
    min-height: 15rem;
    padding: 1.5rem;
  }

  .category-hero-card__title {
    font-size: 1.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .category-hero-card,
  .category-hero-card__image,
  .category-hero-card__cta {
    transition: none;
  }

  .category-hero-card:hover,
  .category-hero-card:focus-visible {
    transform: none;
  }

  .category-hero-card:hover .category-hero-card__image,
  .category-hero-card:focus-visible .category-hero-card__image {
    transform: none;
  }
}
</style>
